import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import type { EventsResponse, LiveSessionEvent, ParticipantsResponse } from './types';

const TAIL_INTERVAL_MS = 5000;
const PAGE_LIMIT = 1000;
const MAX_PAGES = 30;

type QueryParams = Record<string, string | number | undefined>;

const eventsPath = (sessionId: string, params: QueryParams) => (
  Routes.plato_api_events_sysadmin_live_session_path(sessionId, params)
);

const compareEvents = (a: LiveSessionEvent, b: LiveSessionEvent) => {
  if (a.occurred_at !== b.occurred_at) return a.occurred_at < b.occurred_at ? -1 : 1;
  return a.id - b.id;
};

export const mergeEvents = (
  existing: LiveSessionEvent[], incoming: LiveSessionEvent[],
): LiveSessionEvent[] => {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map(e => e.id));
  const fresh = incoming.filter(e => !seen.has(e.id));
  if (fresh.length === 0) return existing;
  return [...existing, ...fresh].sort(compareEvents);
};

// Walks the cursor until the server reports no more rows, so the timeline and the derived lanes
// see the whole session. Bounded so a runaway session cannot hang the page.
export const fetchAllEvents = async (sessionId: string): Promise<LiveSessionEvent[]> => {
  const fetchFrom = async (cursor: string | null, page: number): Promise<LiveSessionEvent[]> => {
    const response = await axios.get<EventsResponse>(
      eventsPath(sessionId, { cursor: cursor ?? undefined, limit: PAGE_LIMIT }),
    );
    const { data, meta } = response.data;
    if (!meta.has_more || !meta.next_cursor || page + 1 >= MAX_PAGES) return data;
    return [...data, ...(await fetchFrom(meta.next_cursor, page + 1))];
  };

  return fetchFrom(null, 0);
};

const fetchTail = async (sessionId: string, afterId: number): Promise<LiveSessionEvent[]> => {
  const response = await axios.get<EventsResponse>(
    eventsPath(sessionId, { after_id: afterId, limit: PAGE_LIMIT }),
  );
  return response.data.data;
};

const maxId = (events: LiveSessionEvent[]) => events.reduce((max, e) => Math.max(max, e.id), 0);

export const useSessionEvents = (sessionId: string, live: boolean) => {
  const [events, setEvents] = useState<LiveSessionEvent[]>([]);
  const maxIdRef = useRef(0);

  const initial = useQuery({
    cacheTime: 0,
    queryFn: () => fetchAllEvents(sessionId),
    queryKey: ['LiveSessionForensics', 'events', sessionId],
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!initial.data) return;
    setEvents(initial.data);
    maxIdRef.current = maxId(initial.data);
  }, [initial.data]);

  const tail = useQuery({
    enabled: live && initial.isSuccess,
    queryFn: () => fetchTail(sessionId, maxIdRef.current),
    queryKey: ['LiveSessionForensics', 'tail', sessionId],
    refetchInterval: live ? TAIL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (!tail.data || tail.data.length === 0) return;
    maxIdRef.current = Math.max(maxIdRef.current, maxId(tail.data));
    setEvents(prev => mergeEvents(prev, tail.data));
  }, [tail.data]);

  return {
    events,
    isError: initial.isError,
    isLoading: initial.isLoading,
    refetch: initial.refetch,
  };
};

export const useSessionParticipants = (sessionId: string, live: boolean) => useQuery({
  queryFn: () => axios
    .get<{ data: ParticipantsResponse }>(Routes.plato_api_participants_sysadmin_live_session_path(sessionId))
    .then(res => res.data.data),
  queryKey: ['LiveSessionForensics', 'participants', sessionId],
  refetchInterval: live ? TAIL_INTERVAL_MS : false,
  refetchIntervalInBackground: false,
});
